import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  LayoutDashboard,
  Users,
  Key,
  BarChart3,
  FileText,
  Activity,
  Settings,
  User,
  BookOpen,
  Wrench,
  ChevronLeft,
  ChevronRight,
  Building2,
  Wallet,
  Settings2,
  Receipt,
  TrendingUp,
  LineChart,
  PieChart,
  TrendingUpDown,
  Monitor,
  FileSearch,
  LayoutGrid,
  Package,
  Layers,
  Sigma,
  AlertTriangle,
  AlertCircle,
} from "lucide-react";
import "./Sidebar.css";
import "../../styles/design-tokens.css";
import Logo from "../common/Logo";

const Sidebar = () => {
  const [collapsed, setCollapsed] = React.useState(() => {
    return localStorage.getItem("sidebar-collapsed") === "true";
  });
  const [expandedKeys, setExpandedKeys] = React.useState(() => {
    const saved = localStorage.getItem("sidebar-expanded-keys");
    return saved ? JSON.parse(saved) : [];
  });
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);

  React.useEffect(() => {
    const adminLayout = document.querySelector(".admin-layout");
    if (adminLayout) {
      adminLayout.classList.toggle("sidebar-collapsed", collapsed);
    }
  }, [collapsed]);

  React.useEffect(() => {
    localStorage.setItem("sidebar-expanded-keys", JSON.stringify(expandedKeys));
  }, [expandedKeys]);

  const menuItems = [
    {
      key: "dashboard",
      path: "/dashboard",
      icon: <LayoutDashboard size={20} />,
      label: "仪表盘",
      role: ["admin", "user"],
    },
    {
      key: "group-overview",
      label: "全景与图表",
      icon: <LayoutGrid size={20} />,
      role: ["admin", "user"],
      children: [
        {
          key: "composite-overview",
          path: "/composite-overview",
          icon: <Activity size={20} />,
          label: "综合全景",
          role: ["admin", "user"],
        },
        {
          key: "trade-chart",
          path: "/trade-chart",
          icon: <BarChart3 size={20} />,
          label: "数据图表",
          role: ["admin", "user"],
        },
        {
          key: "fund-trends",
          path: "/fund-trends",
          icon: <TrendingUp size={20} />,
          label: "资金走势",
          role: ["admin", "user"],
        },
      ],
    },
    {
      key: "group-admin",
      label: "后台管理",
      icon: <Users size={20} />,
      role: ["admin"],
      children: [
        {
          key: "users",
          path: "/users",
          icon: <Users size={20} />,
          label: "用户管理",
          role: ["admin"],
        },
        {
          key: "exchanges",
          path: "/exchanges",
          icon: <Building2 size={20} />,
          label: "交易所管理",
          role: ["admin"],
        },
      ],
    },
    {
      key: "group-accounts",
      label: "账号与交易",
      icon: <Wallet size={20} />,
      role: ["admin", "user"],
      children: [
        {
          key: "balances",
          path: "/balances",
          icon: <Wallet size={20} />,
          label: "账号资金",
          role: ["admin", "user"],
        },
        {
          key: "account-configs",
          path: "/account-configs",
          icon: <Settings2 size={20} />,
          label: "账号配置",
          role: ["admin", "user"],
        },
        {
          key: "tokens",
          path: "/tokens",
          icon: <Key size={20} />,
          label: "API Token",
          role: ["admin", "user"],
        },
        {
          key: "business-lines",
          path: "/business-lines",
          icon: <Layers size={20} />,
          label: "业务线",
          role: ["admin", "user"],
        },
        {
          key: "strategy-types",
          path: "/strategy-types",
          icon: <Sigma size={20} />,
          label: "策略类型",
          role: ["admin", "user"],
        },
        {
          key: "trade-data",
          path: "/trade-data",
          icon: <BarChart3 size={20} />,
          label: "交易数据",
          role: ["admin", "user"],
        },
        {
          key: "transactions",
          path: "/transactions",
          icon: <Receipt size={20} />,
          label: "资金流水",
          role: ["admin", "user"],
        },
        {
          key: "hedge-monitor",
          path: "/hedge-monitor",
          icon: <Activity size={20} />,
          label: "对冲监控",
          role: ["admin", "user"],
        },
      ],
    },
    {
      key: "reports",
      path: "/reports",
      icon: <FileText size={20} />,
      label: "报告生成",
      role: ["admin", "user"],
    },
    {
      key: "raven-download",
      path: "/raven-download",
      icon: <Package size={20} />,
      label: "Raven 工具",
      role: ["admin", "user"],
    },
    {
      key: "analytics",
      label: "数据分析",
      icon: <LineChart size={20} />,
      role: ["admin", "user"],
      children: [
        {
          key: "analytics-compare",
          path: "/analytics/compare",
          icon: <TrendingUpDown size={20} />,
          label: "对比分析",
          role: ["admin", "user"],
        },
        {
          key: "analytics-attribution",
          path: "/analytics/attribution",
          icon: <PieChart size={20} />,
          label: "归因分析",
          role: ["admin", "user"],
        },
        {
          key: "analytics-forecast",
          path: "/analytics/forecast",
          icon: <TrendingUp size={20} />,
          label: "趋势预测",
          role: ["admin", "user"],
        },
      ],
    },
    {
      key: "group-alerts",
      label: "告警中心",
      icon: <AlertTriangle size={20} />,
      role: ["admin", "user"],
      children: [
        {
          key: "alerts",
          path: "/alerts",
          icon: <AlertCircle size={20} />,
          label: "告警列表",
          role: ["admin", "user"],
        },
        {
          key: "alert-rules",
          path: "/alert-rules",
          icon: <Settings size={20} />,
          label: "告警规则",
          role: ["admin"],
        },
      ],
    },
    {
      key: "monitor",
      label: "系统监控",
      icon: <Monitor size={20} />,
      role: ["admin"],
      children: [
        {
          key: "monitor-health",
          path: "/monitor/health",
          icon: <Activity size={20} />,
          label: "健康状态",
          role: ["admin"],
        },
        {
          key: "audit-logs",
          path: "/audit/logs",
          icon: <FileSearch size={20} />,
          label: "审计日志",
          role: ["admin"],
        },
      ],
    },
    {
      key: "group-system",
      label: "系统",
      icon: <Settings size={20} />,
      role: ["admin"],
      children: [
        {
          key: "settings",
          path: "/settings",
          icon: <Settings size={20} />,
          label: "系统设置",
          role: ["admin"],
        },
        {
          key: "online-users",
          path: "/online-users",
          icon: <Activity size={20} />,
          label: "在线用户",
          role: ["admin"],
        },
      ],
    },
    {
      key: "profile",
      path: "/profile",
      icon: <User size={20} />,
      label: "个人中心",
      role: ["admin", "user"],
    },
    {
      key: "api-docs",
      path: "/api-docs",
      icon: <BookOpen size={20} />,
      label: "API文档",
      role: ["admin"],
    },
    {
      key: "debug",
      path: "/debug",
      icon: <Wrench size={20} />,
      label: "API测试",
      role: ["admin"],
    },
  ];

  const userRole = user?.role || "user";

  const filteredMenuItems = menuItems.filter((item) => {
    if (item.children?.length) {
      return item.children.some((child) => child.role.includes(userRole));
    }
    return item.role.includes(userRole);
  });

  const isActive = (path) => {
    return (
      location.pathname === path ||
      (path !== "/dashboard" && location.pathname.startsWith(path))
    );
  };

  // 进入子路由时自动展开所属分组
  React.useEffect(() => {
    const keys = [];
    for (const item of menuItems) {
      if (
        item.children?.some(
          (child) =>
            child.path &&
            (location.pathname === child.path ||
              (child.path !== "/dashboard" &&
                location.pathname.startsWith(child.path))),
        )
      ) {
        keys.push(item.key);
      }
    }
    if (keys.length === 0) return;
    setExpandedKeys((prev) => {
      const toAdd = keys.filter((k) => !prev.includes(k));
      return toAdd.length ? [...prev, ...toAdd] : prev;
    });
  }, [location.pathname]);

  const toggleCollapse = () => {
    const newState = !collapsed;
    setCollapsed(newState);
    localStorage.setItem("sidebar-collapsed", newState);
  };

  const toggleExpand = (key) => {
    setExpandedKeys(prev =>
      prev.includes(key)
        ? prev.filter(k => k !== key)
        : [...prev, key]
    );
  };

  const renderMenuItem = (item, index) => {
    if (item.children) {
      const isExpanded = expandedKeys.includes(item.key);
      const hasActiveChild = item.children.some(child => isActive(child.path));

      return (
        <li key={item.key} className="menu-item menu-item-group">
          <div
            className={`menu-link ${hasActiveChild ? "active" : ""}`}
            onClick={() => !collapsed && toggleExpand(item.key)}
            style={{ animationDelay: `${index * 50}ms`, cursor: 'pointer' }}
          >
            <span className="menu-icon">{item.icon}</span>
            {!collapsed && (
              <>
                <span className="menu-label">{item.label}</span>
                <span className="menu-arrow" style={{ marginLeft: 'auto' }}>
                  {isExpanded ? '▼' : '▶'}
                </span>
              </>
            )}
          </div>
          {!collapsed && isExpanded && (
            <ul className="submenu-list">
              {item.children
                .filter((child) => child.role.includes(userRole))
                .map((child, childIndex) => (
                  <li key={child.key} className="menu-item submenu-item">
                    <Link
                      to={child.path}
                      className={`menu-link ${isActive(child.path) ? "active" : ""}`}
                      style={{ animationDelay: `${(index + childIndex) * 50}ms` }}
                    >
                      <span className="menu-icon">{child.icon}</span>
                      <span className="menu-label">{child.label}</span>
                      {isActive(child.path) && <div className="active-indicator" />}
                    </Link>
                  </li>
                ))}
            </ul>
          )}
        </li>
      );
    }

    return (
      <li key={item.key} className="menu-item">
        <Link
          to={item.path}
          className={`menu-link ${isActive(item.path) ? "active" : ""}`}
          title={collapsed ? item.label : ""}
          style={{ animationDelay: `${index * 50}ms` }}
        >
          <span className="menu-icon">{item.icon}</span>
          {!collapsed && <span className="menu-label">{item.label}</span>}
          {isActive(item.path) && !collapsed && (
            <div className="active-indicator" />
          )}
        </Link>
      </li>
    );
  };

  return (
    <aside className={`sidebar ${collapsed ? "collapsed" : ""}`}>
      {/* 侧边栏头部 */}
      <div className="sidebar-header">
        <div className="logo">
          <div className="logo-icon-wrapper">
            <Logo />
          </div>
          {!collapsed && <span className="logo-text">交易量管理</span>}
        </div>
        <button
          className="collapse-btn"
          onClick={toggleCollapse}
          aria-label={collapsed ? "展开侧边栏" : "收起侧边栏"}
          title={collapsed ? "展开" : "收起"}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>

      {/* 导航菜单 */}
      <nav className="sidebar-nav">
        <ul className="menu-list">
          {filteredMenuItems.map((item, index) => renderMenuItem(item, index))}
        </ul>
      </nav>

      {/* 侧边栏底部用户信息 */}
      <div className="sidebar-footer">
        <div className="user-info">
          <div className="user-avatar">
            <User size={20} />
          </div>
          {!collapsed && (
            <div className="user-details">
              <div className="username">{user?.username}</div>
              <div className="user-role">
                {user?.role === "admin" ? "管理员" : "普通用户"}
              </div>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
